import React from "react";

/**
 * Jira wiki links, `[label|url]` with an optional `|smart-link` tail, and bare
 * URLs. The agents paste links that reach the dashboard in that raw form, with
 * the URL written twice (CTX-1433). The label may hold brackets of its own
 * ("[KS-1648] Outage — Jira"), so it runs lazily up to the first `|http`.
 */
const LINK = /\[([^\n|]+?)\|(https?:\/\/[^\]|\s]+)(?:\|[^\]\n]*)?\]|(https?:\/\/[^\s<>\]]+)/g;

/** Punctuation that ends a sentence, not the URL written before it. */
const TRAILING_PUNCTUATION = /[.,;:!?)]+$/;

/** The issue key a Jira URL ends on ("KS-1665"), if it ends on one. */
const ISSUE_KEY = /\/([A-Z][A-Z0-9]+-\d+)\/?(?:[?#].*)?$/;

/**
 * A link into Jira. A customer has no Jira account, so it only ever reaches
 * Atlassian's login page: the dashboard shows these as text.
 */
export const isJiraUrl = (url: string): boolean => {
  try {
    return new URL(url).hostname.endsWith(".atlassian.net");
  } catch {
    return false;
  }
};

/**
 * What a link reads as: the issue key for a Jira issue, the label the agent
 * wrote when it is not the URL again, else the host and the last path part.
 */
export function jiraLinkLabel(url: string, label?: string): string {
  const key = url.match(ISSUE_KEY)?.[1];
  if (key) return key;
  if (label && label !== url) return label;
  try {
    const { host, pathname } = new URL(url);
    const last = pathname.split("/").filter(Boolean).pop();
    return last ? `${host}/…/${last}` : host;
  } catch {
    return url;
  }
}

export type JiraTextPart =
  | { kind: "text"; text: string }
  | { kind: "link"; url: string; label: string };

/** The text cut into plain runs and links, in order; Jira links become text. */
export function parseJiraText(text: string): JiraTextPart[] {
  const parts: JiraTextPart[] = [];
  let last = 0;
  const pushText = (value: string) => {
    if (!value) return;
    const previous = parts[parts.length - 1];
    if (previous?.kind === "text") previous.text += value;
    else parts.push({ kind: "text", text: value });
  };
  const pushLink = (url: string, label: string) => {
    if (isJiraUrl(url)) pushText(label);
    else parts.push({ kind: "link", url, label });
  };

  for (const match of text.matchAll(LINK)) {
    const start = match.index ?? 0;
    pushText(text.slice(last, start));
    if (match[2]) {
      pushLink(match[2], jiraLinkLabel(match[2], match[1]));
      last = start + match[0].length;
      continue;
    }
    const bare = match[3];
    const url = bare.replace(TRAILING_PUNCTUATION, "");
    pushLink(url, jiraLinkLabel(url));
    last = start + url.length;
  }
  pushText(text.slice(last));
  return parts;
}

/** A ticket text with its links rendered as links, and Jira's as plain text. */
export function JiraText({ text }: { text: string }) {
  return (
    <>
      {parseJiraText(text).map((part, i) =>
        part.kind === "text" ? (
          <React.Fragment key={i}>{part.text}</React.Fragment>
        ) : (
          <a
            key={i}
            href={part.url}
            target="_blank"
            rel="noopener noreferrer"
            className="font-medium text-semantic-text-brand underline hover:opacity-80"
          >
            {part.label}
          </a>
        )
      )}
    </>
  );
}
